import { gql } from "graphql-request";
import { FC, useEffect, useState } from "react";
import { Image } from "components/Image/Image";
import { useScreenIsAboveBreakpoint } from "hooks/useScreenIsAboveBreakpoint";
import { QuoteContent } from "types/graphql/generated";

export interface QuoteProps {
  content: QuoteContent;
}

export const Quote: FC<QuoteProps> = ({ content }) => {
  const { userImage, userName, companyName, quoteText, helperText, hasGradient, bgColor } = content;

  const [overflowClass, setOverflowClass] = useState("overflow-y-clip");
  const hasUserName = !!userName;
  const hasCompanyName = !!companyName;
  const hasUserInfo = userImage?.data?.userImage && (hasUserName || hasCompanyName);
  const hasHelperText = !!helperText;

  // Safari > 15 doesn't support overflow: clip
  useEffect(() => {
    const hasClipSupport = typeof window !== "undefined" ? CSS.supports && CSS.supports("overflow", "clip") : false;

    setOverflowClass(hasClipSupport ? "overflow-y-clip" : "overflow-x-hidden");
  }, []);

  const isMediumBreakpoint = useScreenIsAboveBreakpoint("md");

  return (
    <div className="relative w-full">
      <div
        className={`${overflowClass} relative z-[2] mx-auto flex max-w-screen-xl flex-col px-6 py-10 md:px-8 md:py-16`}
      >
        {hasUserInfo && (
          <div className="mb-6 flex gap-6">
            <Image
              src={userImage.data.userImage.src || "/"}
              alt={userImage.data.userImage.alt || "User logo"}
              width={isMediumBreakpoint ? 75 : 65}
              height={isMediumBreakpoint ? 75 : 65}
              style={{ objectFit: "contain" }}
            />
            {hasUserInfo && (
              <div className="flex flex-col justify-center">
                {hasUserName && <span className="text-lg font-semibold md:text-2xl">{userName}</span>}
                {hasCompanyName && <span className="text-lg md:text-2xl">{companyName}</span>}
              </div>
            )}
          </div>
        )}
        <q className="text-balance text-2xl font-semibold text-black md:text-[32px] md:leading-10">{quoteText}</q>
        {hasHelperText && (
          <span className="mt-8 text-xs font-medium text-fog-80 text-opacity-60 md:mt-10">{helperText}</span>
        )}
        {hasGradient && (
          <div
            data-testid="gradient"
            className="absolute bottom-[-10%] z-[-2] h-[40%] w-full max-w-[1000px] bg-gradient-to-r from-coral-20 from-40% via-dusk-10 via-50% to-fog-15 to-100% blur-3xl will-change-transform md:-left-[10%] md:rounded-r-full"
          />
        )}
      </div>
      <div
        data-testid="background"
        className={`absolute top-0 z-[-10] h-[100%] w-[100%]`}
        style={{ backgroundColor: bgColor ?? "#FFFFFF" }}
      />
    </div>
  );
};

gql`
  fragment QuoteContent on ComponentMoleculesQuote {
    userImage {
      data {
        userImage: attributes {
          src: hash
          alt: alternativeText
          width
          height
          ext
        }
      }
    }
    userName
    companyName
    quoteText
    helperText
    hasGradient
    bgColor
  }
`;
